import { useEffect, useState } from 'react';
import api from '../../services/api';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Select from '../../components/Select';
import Table from '../../components/Table';
import Modal from '../../components/Modal';
import ConfirmDialog from '../../components/ConfirmDialog';

interface Limit {
  id: string;
  code: string;
  name: string;
}
interface HierarchyOption {
  value: string;
  label: string;
  groupId?: string;
  claimId?: string;
  subClaimId?: string;
}
interface Item {
  key: string;
  hierarchyValue: string;
  entitled: boolean;
  perVisitLimit: string;
  monthlyLimit: string;
  yearlyLimit: string;
  escalationThreshold: string;
}
interface Employee {
  id: string;
  code: string;
  first_name: string;
  last_name: string;
}

const EMPTY = { code: '', name: '' };

export default function ClaimLimits() {
  const [limits, setLimits] = useState<Limit[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [deleteTarget, setDeleteTarget] = useState<Limit | null>(null);
  const [error, setError] = useState('');
  const [hierarchyOptions, setHierarchyOptions] = useState<HierarchyOption[]>([]);

  const [itemsTarget, setItemsTarget] = useState<Limit | null>(null);
  const [items, setItems] = useState<Item[]>([]);

  const [assignTarget, setAssignTarget] = useState<Limit | null>(null);
  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
  const [pickEmployeeIds, setPickEmployeeIds] = useState<string[]>([]);

  const load = async () => {
    const [l, g] = await Promise.all([api.get('/ecs/masters/claim-limits'), api.get('/ecs/masters/claim-groups')]);
    setLimits(l.data.claimLimits || []);
    const opts: HierarchyOption[] = [];
    for (const grp of g.data.claimGroups || []) {
      opts.push({ value: `group:${grp.id}`, label: `${grp.name}`, groupId: grp.id });
      for (const c of grp.claims || []) {
        opts.push({ value: `claim:${c.id}`, label: `— ${grp.name} / ${c.name}`, claimId: c.id });
        for (const sc of c.subClaims || []) {
          opts.push({ value: `sub:${sc.id}`, label: `—— ${grp.name} / ${c.name} / ${sc.name}`, subClaimId: sc.id });
        }
      }
    }
    setHierarchyOptions(opts);
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY);
    setError('');
    setShowForm(true);
  };

  const openEdit = (row: Limit) => {
    setEditingId(row.id);
    setForm({ code: row.code, name: row.name });
    setError('');
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      if (editingId) await api.put(`/ecs/masters/claim-limits/${editingId}`, form);
      else await api.post('/ecs/masters/claim-limits', form);
      setShowForm(false);
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await api.delete(`/ecs/masters/claim-limits/${deleteTarget.id}`);
    setDeleteTarget(null);
    load();
  };

  const openItems = async (row: Limit) => {
    setItemsTarget(row);
    const res = await api.get(`/ecs/masters/claim-limits/${row.id}/items`);
    setItems(
      (res.data.items || []).map((it: any, i: number) => ({
        key: String(i),
        hierarchyValue: it.sub_claim_id ? `sub:${it.sub_claim_id}` : it.claim_id ? `claim:${it.claim_id}` : `group:${it.claim_group_id}`,
        entitled: it.entitled,
        perVisitLimit: it.per_visit_limit ?? '',
        monthlyLimit: it.monthly_limit ?? '',
        yearlyLimit: it.yearly_limit ?? '',
        escalationThreshold: it.escalation_threshold ?? '',
      }))
    );
  };

  const addItem = () => {
    setItems((prev) => [
      ...prev,
      { key: String(Date.now()), hierarchyValue: '', entitled: true, perVisitLimit: '', monthlyLimit: '', yearlyLimit: '', escalationThreshold: '' },
    ]);
  };

  const updateItem = (key: string, patch: Partial<Item>) => {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  };

  const removeItem = (key: string) => {
    setItems((prev) => prev.filter((it) => it.key !== key));
  };

  const saveItems = async () => {
    if (!itemsTarget) return;
    const payload = items
      .filter((it) => it.hierarchyValue)
      .map((it) => {
        const [kind, id] = it.hierarchyValue.split(':');
        return {
          claimGroupId: kind === 'group' ? id : null,
          claimId: kind === 'claim' ? id : null,
          subClaimId: kind === 'sub' ? id : null,
          entitled: it.entitled,
          perVisitLimit: it.perVisitLimit || null,
          monthlyLimit: it.monthlyLimit || null,
          yearlyLimit: it.yearlyLimit || null,
          escalationThreshold: it.escalationThreshold || null,
        };
      });
    await api.put(`/ecs/masters/claim-limits/${itemsTarget.id}/items`, { items: payload });
    setItemsTarget(null);
  };

  const openAssign = async (row: Limit) => {
    setAssignTarget(row);
    setPickEmployeeIds([]);
    const res = await api.get('/employees');
    setAllEmployees(res.data.employees || []);
  };

  const saveAssign = async () => {
    if (!assignTarget || !pickEmployeeIds.length) return;
    await api.post(`/ecs/masters/claim-limits/${assignTarget.id}/assign`, { employeeIds: pickEmployeeIds });
    setAssignTarget(null);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-gray-800">Claim Limit</h1>
        <Button onClick={openCreate}>+ New</Button>
      </div>
      <Table
        columns={[
          { key: 'code', header: 'Code' },
          { key: 'name', header: 'Name' },
          {
            key: 'actions',
            header: 'Actions',
            render: (row) => (
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => openEdit(row)}>
                  Edit
                </Button>
                <Button variant="ghost" onClick={() => openItems(row)}>
                  Limits
                </Button>
                <Button variant="ghost" onClick={() => openAssign(row)}>
                  Assign
                </Button>
                <Button variant="ghost" className="text-red-600" onClick={() => setDeleteTarget(row)}>
                  Delete
                </Button>
              </div>
            ),
          },
        ]}
        rows={limits}
      />

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editingId ? 'Edit Claim Limit' : 'New Claim Limit'}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {error && <div className="text-sm text-red-600">{error}</div>}
          <Input label="Code" maxLength={6} required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          <Input label="Name" maxLength={50} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <div className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button type="submit">Save</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={!!itemsTarget} onClose={() => setItemsTarget(null)} title={`Limits — ${itemsTarget?.name}`}>
        <div className="flex flex-col gap-3">
          {items.map((it) => (
            <div key={it.key} className="grid grid-cols-6 gap-2 items-end border-b pb-2">
              <div className="col-span-2">
                <Select
                  label="Claim"
                  options={hierarchyOptions}
                  value={it.hierarchyValue}
                  onChange={(e) => updateItem(it.key, { hierarchyValue: e.target.value })}
                />
              </div>
              <label className="flex items-center gap-1 text-xs">
                <input type="checkbox" checked={it.entitled} onChange={(e) => updateItem(it.key, { entitled: e.target.checked })} /> Entitled
              </label>
              <Input label="Per Visit" type="number" value={it.perVisitLimit} onChange={(e) => updateItem(it.key, { perVisitLimit: e.target.value })} />
              <Input label="Monthly" type="number" value={it.monthlyLimit} onChange={(e) => updateItem(it.key, { monthlyLimit: e.target.value })} />
              <Input label="Yearly" type="number" value={it.yearlyLimit} onChange={(e) => updateItem(it.key, { yearlyLimit: e.target.value })} />
              <button className="text-red-600 text-xs" onClick={() => removeItem(it.key)}>
                Remove
              </button>
            </div>
          ))}
          <Button variant="secondary" onClick={addItem}>
            + Add Row
          </Button>
          <Button onClick={saveItems}>Save Limits</Button>
        </div>
      </Modal>

      <Modal isOpen={!!assignTarget} onClose={() => setAssignTarget(null)} title={`Assign Employees — ${assignTarget?.name}`}>
        <div className="flex flex-col gap-4">
          <select
            multiple
            className="border rounded-lg p-2 h-40"
            value={pickEmployeeIds}
            onChange={(e) => setPickEmployeeIds(Array.from(e.target.selectedOptions).map((o) => o.value))}
          >
            {allEmployees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.code} — {emp.first_name} {emp.last_name}
              </option>
            ))}
          </select>
          <Button onClick={saveAssign}>Assign Selected</Button>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        message={`Delete claim limit "${deleteTarget?.name}"?`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
